import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

/**
 * Marquee-style ticker that scrolls recent anonymized platform activity.
 * Pulls from /api/activity/recent, animates with CSS keyframes, pauses on hover.
 */
export default function RecentActivityTicker() {
  const { t, i18n } = useTranslation();
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/activity/recent", { params: { limit: 12 } });
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    load();
    const interval = setInterval(load, 20000); // refresh every 20s
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (items.length === 0) return null;

  // Duplicate items so the marquee loops seamlessly
  const loop = [...items, ...items];

  const labelFor = (type) => {
    switch (type) {
      case "coin_purchase":  return t("ticker_purchase");
      case "advance_payout": return t("ticker_funded");
      case "distribution":   return t("ticker_distribution");
      case "rent_payment":   return t("ticker_rent");
      default: return type;
    }
  };

  const dotColor = (type) => ({
    coin_purchase:  "#002FA7",
    advance_payout: "#FFCC00",
    distribution:   "#16A34A",
    rent_payment:   "#525252",
  }[type] || "#525252");

  return (
    <div
      data-testid="activity-ticker"
      className="border-b border-black/10 bg-zinc-50 overflow-hidden relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 flex items-center gap-4 h-10">
        <div className="font-mono-label text-[10px] text-[#002FA7] shrink-0 flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse" />
          {t("ticker_tag")}
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div
            ref={trackRef}
            className="flex gap-10 items-center whitespace-nowrap"
            style={{
              animation: `ticker-scroll ${Math.max(20, items.length * 4)}s linear infinite`,
              direction: "ltr",
            }}
          >
            {loop.map((it, i) => (
              <div
                key={`${it.created_at}-${i}`}
                className="flex items-center gap-2 text-[11px] font-mono-label text-zinc-700"
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: dotColor(it.type) }}
                />
                <span className="text-zinc-500">{labelFor(it.type)}</span>
                <span className="text-zinc-800 font-bold tabular-nums">
                  {fmt(it.amount, { maxDecimals: 0 })}
                </span>
                {it.description && (
                  <span className="text-zinc-400 max-w-xs truncate">· {it.description}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="activity-ticker"]:hover [class*="ticker-scroll"] {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
